import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { startVoiceSession, voiceStreamUrl } from './api';
import { createVoiceAudio } from './voiceAudio';
import { createVoiceSession } from './voiceSession';
import './Voice.css';

// How close to the end of the record counts as reading the end of it. A couple of lines' worth:
// enough that revealing another character does not count as having scrolled away.
const FOLLOW_MARGIN_PX = 48;

// Speak to a persona and hear it answer. The screen is deliberately wordless: the buttons are the
// whole of what it says, so nothing here will need translating when the real screen is designed.
// It does not name the persona either — the student picked it a moment ago, and the persona says
// who it is out loud, which is what it greets first for.
//
// The persona arrives as a prop rather than off the router, so this stays testable without one.
// A scene passes [openSession] instead: who is spoken to there is a role in a run, and only the
// way the ticket is minted differs — everything below this line is the same conversation.
//
// A scene also passes [onScene], which is what the character did to it, and [openOnMount], which
// is a screen saying the gesture that mounted this is the gesture that opened the microphone. It
// may pass [onSpeaking] too, which is whether the character is talking at this moment — what a
// screen with something to do at the end of an answer waits on.
//
// An overlay passes [onClose]: a close button stands in the power button's place — closing is
// stopping, and the only way back. It also passes [greeted=false] — the relay greets characters
// and leaves a teacher silent — with [idleCaption], shown in the empty record once the session
// is ready: the one sign a silent handoff worked. The caption arrives already translated, being
// the caller's words; the close button wears the app-wide Close, translated here like the error
// toasts.
//
// A screen with nobody in it to talk to passes [microphone=false]: the device is never asked for,
// so no permission prompt is raised, and nothing is offered to hold down.
//
// The screen the overlay covers passes [hidden], mirroring the attribute that hides this Voice:
// the session stays open — hiding is not stopping — but the talk gate closes and will not
// reopen, so nothing said over the overlay reaches the session underneath.
//
// Everything with a rule in it lives in voiceSession.js; this is the wiring between it, a
// WebSocket and a pair of buttons.
function Voice({
  personaId,
  openSession,
  onScene,
  onSpeaking,
  openOnMount = false,
  greeted = true,
  microphone = true,
  idleCaption,
  onClose,
  hidden = false,
}) {
  const { t } = useTranslation();
  const [state, setState] = useState('stopped');
  const [talking, setTalking] = useState(false);
  const [turns, setTurns] = useState([]);
  const sessionRef = useRef(null);
  const recordRef = useRef(null);
  // Whether the record is still following the conversation. Read before each render's scroll, so
  // it says where the student had put the view, not where the last reveal left it.
  const followingRef = useRef(true);
  // The same fact as `talking`, readable from an event handler that closed over an older render.
  const talkingRef = useRef(false);
  // And the same again for `hidden`.
  const hiddenRef = useRef(hidden);

  // Every way a hold can end funnels through here, including the ones the user did not choose:
  // dragging off the button, a cancelled touch, the window losing focus. A press whose release
  // never arrives leaves the microphone on the wire, and that costs money.
  const release = useCallback(() => {
    if (!talkingRef.current) {
      return;
    }
    talkingRef.current = false;
    setTalking(false);
    sessionRef.current?.releaseTalk();
  }, []);

  const press = useCallback(() => {
    if (hiddenRef.current || talkingRef.current || !sessionRef.current) {
      return;
    }
    talkingRef.current = true;
    setTalking(true);
    sessionRef.current.pressTalk();
  }, []);

  const stop = useCallback(() => {
    release();
    sessionRef.current?.stop();
    sessionRef.current = null;
  }, [release]);

  const start = useCallback(async () => {
    // Claimed before the first await: nothing on screen changes until the microphone has been
    // asked for, so a second click would otherwise open a second session — leaving the first one
    // recording, and its relay socket billed, with no handle left to close it.
    if (sessionRef.current) {
      return;
    }

    const session = createVoiceSession({
      // The ticket is single-use and short-lived, so it is minted inside the session's own
      // sequence — after the microphone is granted — rather than before the permission prompt.
      openSocket: async () => {
        const { ticket } = await (openSession ? openSession() : startVoiceSession(personaId));
        return new WebSocket(voiceStreamUrl(ticket));
      },
      audio: createVoiceAudio(),
      onState: setState,
      onTurns: setTurns,
      // Autolang's own frame coming down: what the character just did, as the server sees it.
      // Nothing on this screen shows it — a scene screen is what has somewhere to put it.
      onScene,
      onSpeaking,
      greeted,
      microphone,
      // Every way a session fails arrives here as one word, whichever end it failed at: a
      // microphone that would not open, a ticket already spent, an upstream that died. A toast
      // rather than anything on the screen — the buttons and the record are wordless on purpose,
      // and this is the only thing here that has ever needed translating.
      onFailure: (reason) => {
        // The session has already stopped itself. What is left is the handle this screen holds:
        // without clearing it, Start would quietly refuse to open another.
        sessionRef.current = null;
        setState('stopped');
        toast.error(t(`voice.errors.${reason}`));
        if (reason === 'allowanceExhausted') {
          // The announcement every AI call already makes on a 402, so a conversation cut off for
          // want of credit reaches the same funnel as everything else. Deliberately with no
          // message: the screen it lands on has its own, and in the right language.
          window.dispatchEvent(new CustomEvent('ai:capped', { detail: {} }));
        }
      },
    });
    sessionRef.current = session;

    try {
      await session.start();
    } catch (error) {
      // What is left after onFailure: a ticket that could not be minted at all.
      console.error('Could not start the voice session', error);
      session.stop();
      sessionRef.current = null;
      setState('stopped');
      // The 402 is announced app-wide by authedFetch, which moves the user on its own; saying it
      // again here would be the same refusal twice, in two places. Everything else is said here or
      // nowhere — and nowhere is what it was, which left the student pressing Start and watching
      // nothing happen.
      if (error?.status !== 402) {
        toast.error(
          t(error?.status === 404 ? 'voice.errors.personaUnavailable' : 'voice.errors.couldNotStart')
        );
      }
    }
  }, [greeted, microphone, onScene, onSpeaking, openSession, personaId, t]);

  // A screen that opens on the way in was mounted by a click, and that click is what the browser
  // will hang the microphone prompt off. Once: reopening is what the Start button is for.
  useEffect(() => {
    if (openOnMount) {
      start();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The newest turn is the one being spoken, so the record follows it down — but only while the
  // student is reading the end of it. The reveal republishes ten times a second while the AI
  // speaks, and scrolling back to the bottom on every one of those would make reading anything
  // earlier impossible, which is most of what a record is for.
  useEffect(() => {
    const record = recordRef.current;
    if (record && followingRef.current) {
      record.scrollTop = record.scrollHeight;
    }
  }, [turns]);

  // Fires for the student's own scrolling and for the effect above, so leaving the end stops the
  // following and coming back to it starts again.
  const onRecordScroll = (event) => {
    const record = event.currentTarget;
    const below = record.scrollHeight - record.scrollTop - record.clientHeight;
    followingRef.current = below < FOLLOW_MARGIN_PX;
  };

  useEffect(() => {
    const onHidden = () => release();
    window.addEventListener('blur', onHidden);
    document.addEventListener('visibilitychange', onHidden);
    return () => {
      window.removeEventListener('blur', onHidden);
      document.removeEventListener('visibilitychange', onHidden);
    };
  }, [release]);

  // Being hidden ends any hold in flight and keeps a new one from opening: a press can still be
  // down as an overlay arrives, and a keyboard can still reach a focused button the overlay only
  // covered — the browser's own blur on an element leaving the page is not something to bank on.
  useEffect(() => {
    hiddenRef.current = hidden;
    if (hidden) {
      release();
    }
  }, [hidden, release]);

  // Leaving the screen with a session open would keep paying for it. Forgotten as well as stopped:
  // React mounts twice in development, and a handle left behind is one the remount's own start
  // refuses to replace — which leaves a screen that opens on mount with a Start button that never
  // opens anything.
  useEffect(
    () => () => {
      sessionRef.current?.stop();
      sessionRef.current = null;
    },
    [],
  );

  const onPointerDown = (event) => {
    // Capture, so the release still reaches this button when the finger slides off it.
    event.currentTarget.setPointerCapture?.(event.pointerId);
    press();
  };

  const onKeyDown = (event) => {
    if (event.key === ' ' || event.key === 'Enter') {
      // Space scrolls the page otherwise, and auto-repeat would press over and over.
      event.preventDefault();
      if (!event.repeat) {
        press();
      }
    }
  };

  const onKeyUp = (event) => {
    if (event.key === ' ' || event.key === 'Enter') {
      release();
    }
  };

  const running = state === 'connecting' || state === 'ready';

  const close = () => {
    stop();
    onClose();
  };

  return (
    <div className="voice">
      {/* Wordless, like the buttons: who spoke is the side and the colour, a turn still coming is
          an ellipsis, words the AI never got to say are struck through, and a turn whose words are
          lost is faded. Nothing here will need translating. */}
      {/* Not a live region. The words are revealed a character at a time as the AI speaks, so
          announcing them would read partial words ten times a second, over the top of the voice
          already saying them. The record is here to be read back, which a screen reader does on
          its own terms. */}
      <div className="voice-record" ref={recordRef} onScroll={onRecordScroll}>
        {/* Render-only: never a turn, so it can never reach any record. The first words replace
            it. */}
        {idleCaption && state === 'ready' && turns.length === 0 && (
          <div className="voice-idle">{idleCaption}</div>
        )}
        {turns.map((turn) => (
          <div
            key={turn.id}
            className={`voice-turn voice-turn--${turn.speaker}${turn.lost ? ' voice-turn--lost' : ''}`}
          >
            {turn.text}
            {turn.struck && <s className="voice-turn-unheard">{turn.struck}</s>}
            {turn.pending && <span className="voice-turn-waiting" aria-hidden="true" />}
          </div>
        ))}
      </div>
      {/* An overlay's close stops before telling the caller, so the session never outlives the
          overlay whatever the caller does next — unmounting stops it a second, idle, time. */}
      <button
        type="button"
        className="voice-power"
        onClick={onClose ? close : running ? stop : start}
      >
        {onClose ? t('common.close') : running ? 'Stop' : 'Start'}
      </button>
      {/* Nothing to hold down where no microphone was opened: a listen-only session has nobody
          in it to talk to, and the button would offer a turn that could only commit silence. */}
      {microphone && (
        <button
          type="button"
          className={talking ? 'voice-talk voice-talk--held' : 'voice-talk'}
          disabled={state !== 'ready'}
          onPointerDown={onPointerDown}
          onPointerUp={release}
          onPointerCancel={release}
          onKeyDown={onKeyDown}
          onKeyUp={onKeyUp}
          onBlur={release}
        >
          Hold to talk
        </button>
      )}
    </div>
  );
}

export default Voice;
